import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Brain, Clock, AlertTriangle, CheckCircle2, RefreshCcw } from 'lucide-react';

/**
 * Per-agent memory feed (Phase O P0).
 *
 * Renders the operative's reflection log — what its brain learned from
 * past memory-aware tasks (signup_gmail, signup_reddit_via_google).
 * The worker's `BrowserUseBrain.run_with_memory` writes one entry to
 * R2 after each task; this view tails it. Entries are ordered
 * oldest→newest in the API; we reverse for display so the latest
 * lesson is at the top.
 */
export function AgentMemory({ agentId }) {
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['agent-memory', agentId],
    queryFn: () => api.get(`/agents/${agentId}/memory?limit=50`).then((r) => r.data),
    refetchInterval: 30_000,
  });

  const entries = (data?.entries || []).slice().reverse();

  return (
    <div className="space-y-3" data-testid="agent-memory-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Brain className="h-4 w-4 text-[hsl(var(--primary))]" />
          <h3 className="text-sm font-semibold text-[hsl(var(--text-1))]">Operative memory</h3>
          <span className="text-[11px] text-[hsl(var(--text-3))]">
            {entries.length} reflection{entries.length === 1 ? '' : 's'}
          </span>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          className="text-[11px] text-[hsl(var(--text-3))] hover:text-[hsl(var(--text-1))] flex items-center gap-1"
          data-testid="memory-refresh-button"
        >
          <RefreshCcw className={`h-3 w-3 ${isFetching ? 'animate-spin' : ''}`} /> refresh
        </button>
      </div>

      <div className="rounded-md border border-white/5 bg-[hsl(var(--surface-2))] p-3 text-[11px] text-[hsl(var(--text-3))]">
        Each entry is a 2-3-sentence post-mortem the LLM wrote about its own task. On the next
        memory-aware task, the operative sees the most recent ones in its system prompt — so it
        can avoid repeating the same mistake. Today: <code className="font-mono text-[10px]">signup_gmail</code> and{' '}
        <code className="font-mono text-[10px]">signup_reddit_via_google</code> write here. Other tasks
        (login_check, warmup) don't yet.
      </div>

      {isLoading ? (
        <div className="text-xs text-[hsl(var(--text-3))]">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="rounded-md border border-dashed border-white/10 bg-[hsl(var(--surface-2))] p-6 text-center text-xs text-[hsl(var(--text-3))]" data-testid="memory-empty">
          This operative hasn't run any memory-aware tasks yet. After it does its first
          <code className="font-mono text-[10px] mx-1">signup_gmail</code> or
          <code className="font-mono text-[10px] mx-1">signup_reddit_via_google</code>,
          a reflection will appear here.
        </div>
      ) : (
        <ul className="space-y-2">
          {entries.map((e, idx) => (
            <MemoryEntry key={`${e.ts || ''}-${idx}`} entry={e} latest={idx === 0} />
          ))}
        </ul>
      )}
    </div>
  );
}

function MemoryEntry({ entry, latest }) {
  const ok = entry.outcome === 'ok';
  const Icon = ok ? CheckCircle2 : AlertTriangle;
  const tone = ok ? 'success' : 'danger';
  const ts = entry.ts ? new Date(entry.ts) : null;
  return (
    <li
      className={`rounded-md border p-3 ${latest ? 'border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/5' : 'border-white/10 bg-[hsl(var(--surface-2))]'}`}
      data-testid="memory-entry"
    >
      <div className="flex items-center gap-2 text-[11px] text-[hsl(var(--text-3))] mb-1.5">
        <Icon className={`h-3.5 w-3.5 text-[hsl(var(--${tone}))]`} />
        <span className="font-mono text-[10px] tabular">
          {entry.task_kind || 'task'}
        </span>
        {entry.verdict && (
          <span className="font-mono text-[10px] text-[hsl(var(--text-3))]/80 truncate max-w-[40%]">
            verdict: {entry.verdict}
          </span>
        )}
        <span className="ml-auto flex items-center gap-1 text-[10px]">
          <Clock className="h-2.5 w-2.5" />
          {ts ? ts.toLocaleString() : '—'}
        </span>
      </div>
      <div className="text-sm text-[hsl(var(--text-1))] whitespace-pre-wrap">{entry.lesson || <span className="italic text-[hsl(var(--text-3))]">(no lesson)</span>}</div>
      {entry.model && (
        <div className="mt-1.5 text-[10px] text-[hsl(var(--text-3))]/60 font-mono">via {entry.model}</div>
      )}
    </li>
  );
}
